import type { SurgeProtectionRunCreateRequest } from "../services/surgeProtection";
import { downloadRunJson } from "../services/runExport";
import { FaultLinkPanel, SHORT_CIRCUIT_CURRENT_WORDING } from "../components/FaultLinkPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { SurgeProtectionForm } from "../components/SurgeProtectionForm";
import { SurgeProtectionResultPanel } from "../components/SurgeProtectionResultPanel";
import { SurgeProtectionResultSummary } from "../components/SurgeProtectionResultSummary";
import { SurgeProtectionWarningPanel } from "../components/SurgeProtectionWarningPanel";
import { useSldElements } from "../hooks/useSldElements";
import { useSurgeProtection } from "../hooks/useSurgeProtection";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The surge protection study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside a result-first
// column - summary, the fault link, the SLD link, warnings, traceability, then the detail tables.
// EOS-10's one page: the SPD of one location against the declared figures.
export function SurgeProtectionPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useSurgeProtection();
  // The active revision's single-line diagram, for the bus picker (A27 (b)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: SurgeProtectionRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-surge-protection-page>
      <header>
        <p>EOS-10 · Surge protection</p>
        <h1>Surge protection</h1>
        <p>
          Check one surge protective device at one location: the effective protection level (the
          declared Up plus the voltage drop across the declared connecting leads) against each
          protected item's declared rated impulse withstand voltage, Uc and UT against their
          declared requirements, the discharge parameters the SPD type carries against the declared
          requirements, and the assembly's short-circuit rating against the prospective current of
          the linked fault run or the declaration, using the validated KES Electrical OS
          calculation service. Every requirement is a declared figure with its source; nothing is
          assumed.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="surge-protection-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="surge-protection-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <SurgeProtectionForm disabled={isPending} onSubmit={handleSubmit} sldElements={sldElements} />
          </details>
        </section>

        <section aria-labelledby="surge-protection-results-heading" data-study-results>
          <h2 id="surge-protection-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Checking the surge protection…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <SurgeProtectionResultSummary result={result} />
              {run ? <FaultLinkPanel link={run.fault_link ?? null} wording={SHORT_CIRCUIT_CURRENT_WORDING} /> : null}
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <SurgeProtectionWarningPanel result={result} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <SurgeProtectionResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to check the surge protection.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="surge-protection-review-heading">
        <h2 id="surge-protection-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance declaration.
          Confirm the SPD manufacturer's data, the equipment data sheets' rated impulse withstand
          voltages, every declared requirement with its source and the linked fault study before
          approval or issue. Whether an SPD is required, the lightning protection zones, the back-up
          device's own rating, the earth lead, the status indication and the coordination with
          every other SPD are not evaluated here.
        </p>
      </section>
    </main>
  );
}

export default SurgeProtectionPage;
